import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, ChevronDown, Loader2 } from 'lucide-react';
import type { ChatResponse } from '../../api/chat';
import { sendChatMessage, getChatStatus } from '../../api/chat';
import ChartRenderer from './ChartRenderer';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  data?: Record<string, unknown>[] | null;
  chartConfig?: ChatResponse['chartConfig'];
  cubeQuery?: Record<string, unknown> | null;
  error?: string;
}

export default function ChatPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [available, setAvailable] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedQuery, setExpandedQuery] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Check availability on mount
  useEffect(() => {
    getChatStatus().then((s) => setAvailable(s.available)).catch(() => setAvailable(false));
  }, []);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const q = input.trim();
    if (!q || loading) return;

    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: q }]);
    setLoading(true);

    try {
      const response = await sendChatMessage(q);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: response.answer,
          data: response.data,
          chartConfig: response.chartConfig,
          cubeQuery: response.cubeQuery,
          error: response.error,
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Something went wrong. Please try again.', error: 'network_error' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  if (!available) return null;

  // Collapsed: floating button
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-moss-600 hover:bg-moss-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-105"
        title="Ask AI"
      >
        <MessageCircle size={24} />
      </button>
    );
  }

  // Expanded: chat panel
  return (
    <div className="fixed bottom-6 right-6 z-50 w-[420px] h-[560px] bg-white dark:bg-[#1a1f1c] rounded-xl shadow-2xl border border-stone-200 dark:border-stone-700 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-moss-600 text-white">
        <div className="flex items-center gap-2">
          <MessageCircle size={18} />
          <span className="font-medium text-sm">Ask AI</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-moss-700 rounded">
            <ChevronDown size={18} />
          </button>
          <button onClick={() => { setIsOpen(false); setMessages([]); }} className="p-1 hover:bg-moss-700 rounded">
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <MessageCircle className="text-stone-300 dark:text-stone-600 mx-auto mb-3" size={32} />
            <p className="text-sm text-stone-500 dark:text-stone-400 font-medium">Ask about your marketing data</p>
            <div className="mt-3 space-y-1.5">
              {[
                'What\'s our ROAS by platform this month?',
                'Show fill rates by region',
                'Which campaigns have the best ROAS?',
              ].map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => { setInput(suggestion); }}
                  className="block w-full text-left px-3 py-1.5 text-xs text-moss-600 dark:text-moss-400 bg-moss-50 dark:bg-moss-900/20 rounded-lg hover:bg-moss-100 dark:hover:bg-moss-900/30 transition"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[90%] rounded-lg px-3 py-2 text-sm ${
                msg.role === 'user'
                  ? 'bg-moss-600 text-white'
                  : 'bg-stone-100 dark:bg-[#242d28] text-bark-900 dark:text-stone-200'
              }`}
            >
              <p className="whitespace-pre-wrap">{msg.content}</p>

              {/* Chart */}
              {msg.data && msg.chartConfig && (
                <div className="mt-2 bg-white dark:bg-[#1a1f1c] rounded-lg p-2 border border-stone-200 dark:border-stone-700">
                  <ChartRenderer
                    type={msg.chartConfig.type}
                    data={msg.data}
                    measures={msg.chartConfig.measures}
                    dimensions={msg.chartConfig.dimensions}
                  />
                </div>
              )}

              {/* View Query */}
              {msg.cubeQuery && (
                <div className="mt-1">
                  <button
                    onClick={() => setExpandedQuery(expandedQuery === i ? null : i)}
                    className="text-[10px] text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 underline"
                  >
                    {expandedQuery === i ? 'Hide query' : 'View query'}
                  </button>
                  {expandedQuery === i && (
                    <pre className="mt-1 text-[10px] bg-stone-50 dark:bg-[#1a1f1c] p-2 rounded overflow-x-auto max-h-[120px] text-stone-600 dark:text-stone-400">
                      {JSON.stringify(msg.cubeQuery, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-stone-100 dark:bg-[#242d28] rounded-lg px-3 py-2 flex items-center gap-2">
              <Loader2 size={14} className="animate-spin text-moss-500" />
              <span className="text-xs text-stone-500">Analyzing...</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-stone-200 dark:border-stone-700">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder="Ask about your data..."
            disabled={loading}
            className="flex-1 text-sm px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-[#1a1f1c] text-bark-900 dark:text-stone-200 placeholder:text-stone-400 focus:outline-none focus:ring-1 focus:ring-moss-500"
          />
          <button
            onClick={handleSend}
            disabled={loading || !input.trim()}
            className="p-2 bg-moss-600 hover:bg-moss-700 disabled:bg-stone-300 dark:disabled:bg-stone-700 text-white rounded-lg transition"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
